import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { APIService } from '../../services/api/api.service';
import { NotificationService } from '../../services/notification.service';
import {
  RTL_LANGUAGE_CODES,
  TRANSLATION_BASE_LANGUAGE,
  TranslationLanguage,
  translationLanguageMeta,
} from '../../models/translations/translation-languages.const';
import { TranslationLanguageSetting } from '../../models/translations/translation-language-setting.model';
import { FlagComponent } from '../flag/flag.component';
import { ModalComponent } from '../modal/modal.component';

export interface TranslationEntry {
  description?: string;
  label?: string;
}

export type TranslationsDictionary = { [lang: string]: TranslationEntry };

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FlagComponent, FormsModule, ModalComponent, RouterModule, TranslateModule],
  selector: 'app-translations-modal',
  standalone: true,
  styleUrls: ['./translations-modal.component.scss'],
  templateUrl: './translations-modal.component.html',
})
export class TranslationsModalComponent implements OnInit, OnDestroy {
  @Input() public baseDescription: string = '';
  @Input() public baseLabel: string = '';
  @Input() public translations: TranslationsDictionary = {};
  @Output() public translationsChange: EventEmitter<TranslationsDictionary> =
    new EventEmitter<TranslationsDictionary>();

  @ViewChild('modal') public modal?: ModalComponent;

  public aiBusy: boolean = false;
  public baseLanguage: TranslationLanguage = TRANSLATION_BASE_LANGUAGE;
  public isOpen: boolean = false;
  public languages: string[] = [];

  private destroy$: Subject<void> = new Subject<void>();

  public constructor(
    private api: APIService,
    private notification: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.loadLanguages();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get hasDescription(): boolean {
    return !!(this.baseDescription && this.baseDescription.trim());
  }

  public get totalCells(): number {
    return this.languages.length * (this.hasDescription ? 2 : 1);
  }

  public get missingCount(): number {
    let count: number = 0;
    this.languages.forEach((code: string): void => {
      const entry: TranslationEntry = this.translations[code] ?? {};
      if (!(entry.label ?? '').trim()) {
        count += 1;
      }
      if (this.hasDescription && !(entry.description ?? '').trim()) {
        count += 1;
      }
    });
    return count;
  }

  public get filledCells(): number {
    return this.totalCells - this.missingCount;
  }

  public get translatedLanguageCount(): number {
    return this.languages.filter(
      (code: string): boolean => !!(this.translations[code]?.label ?? '').trim(),
    ).length;
  }

  public langMeta(code: string): TranslationLanguage {
    return translationLanguageMeta(code);
  }

  public isRtl(code: string): boolean {
    return RTL_LANGUAGE_CODES.includes(code);
  }

  public isLanguageDone(code: string): boolean {
    return !!(this.translations[code]?.label ?? '').trim();
  }

  public isLabelMissing(code: string): boolean {
    return !(this.translations[code]?.label ?? '').trim();
  }

  public isDescriptionMissing(code: string): boolean {
    return this.hasDescription && !(this.translations[code]?.description ?? '').trim();
  }

  public open(): void {
    this.isOpen = true;
    this.loadLanguages();
    if (this.modal && typeof this.modal.openModal === 'function') {
      this.modal.openModal();
    }
    this.cdr.markForCheck();
  }

  public close(): void {
    this.isOpen = false;
    if (this.modal && typeof this.modal.closeModal === 'function') {
      this.modal.closeModal();
    }
    this.cdr.markForCheck();
  }

  public setCell(code: string, field: keyof TranslationEntry, value: string): void {
    const next: TranslationsDictionary = { ...this.translations };
    next[code] = { ...(next[code] ?? {}), [field]: value };
    this.translations = next;
    this.translationsChange.emit(next);
    this.cdr.markForCheck();
  }

  public autoTranslate(): void {
    if (this.languages.length === 0) {
      return;
    }
    if (!this.baseLabel || !this.baseLabel.trim()) {
      this.notification.error('PREFERENCES.TRANSLATIONS_NOTHING_TO_TRANSLATE');
      return;
    }
    const targets: string[] = this.languages.filter((code: string): boolean =>
      this.isLabelMissing(code) || this.isDescriptionMissing(code),
    );
    if (targets.length === 0) {
      this.notification.success('PREFERENCES.TRANSLATIONS_NOTHING_TO_TRANSLATE');
      return;
    }
    const baseTexts: { [key: string]: string } = { label: this.baseLabel };
    if (this.hasDescription) {
      baseTexts['description'] = this.baseDescription;
    }
    this.aiBusy = true;
    this.api.translations
      .autoTranslate(baseTexts, targets)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (): void => {
          this.aiBusy = false;
          this.notification.error('PREFERENCES.TRANSLATIONS_AI_FAILED');
          this.cdr.markForCheck();
        },
        next: (resp: { [key: string]: { [lang: string]: string } }): void => {
          this.applyAutoTranslations(resp, targets);
          this.aiBusy = false;
          this.notification.success('PREFERENCES.TRANSLATIONS_GENERATED');
          this.cdr.markForCheck();
        },
      });
  }

  public trackByLang(_index: number, code: string): string {
    return code;
  }

  private loadLanguages(): void {
    this.api.translationSettings
      .get()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (setting: TranslationLanguageSetting): void => {
          this.languages = setting?.languages ?? [];
          this.cdr.markForCheck();
        },
      });
  }

  private applyAutoTranslations(
    resp: { [key: string]: { [lang: string]: string } },
    targets: string[],
  ): void {
    const next: TranslationsDictionary = { ...this.translations };
    const labelMap: { [lang: string]: string } = resp['label'] ?? {};
    const descriptionMap: { [lang: string]: string } = resp['description'] ?? {};
    targets.forEach((code: string): void => {
      const entry: TranslationEntry = { ...(next[code] ?? {}) };
      if (!(entry.label ?? '').trim() && labelMap[code]) {
        entry.label = labelMap[code];
      }
      if (this.hasDescription && !(entry.description ?? '').trim() && descriptionMap[code]) {
        entry.description = descriptionMap[code];
      }
      next[code] = entry;
    });
    this.translations = next;
    this.translationsChange.emit(next);
  }
}
